import { createMiddleware } from '@tanstack/react-start';

import { sessionMiddleware } from './session';
import { setupMiddleware } from './setup';

import { REFRESH_TOKEN_THRESHOLD_MS } from '@/constants';
import { authResponseToSessionData } from '@/server/auth/utils';
import type { TLAIRefreshTokenResponse } from '@/types/api';
import { apiClient } from '@/utils/api-client';
import { createFormData, extractHTTPError } from '@/utils/http-client';
import { mutateResponse } from '@/utils/response';
import { safeInvoke } from '@/utils/safe-invoke';
import { clearSessionDataHeaders, updateSessionDataHeaders } from '@/utils/session';

export const authTokenRefreshMiddleware = createMiddleware()
  .middleware([setupMiddleware, sessionMiddleware])
  .server(async ({ next, context: { session, backendBaseUrl } }) => {
    if (!session.authentication || !backendBaseUrl) return next();

    const msUntilExpiry = session.authentication.expiresAt - Date.now();

    if (msUntilExpiry > REFRESH_TOKEN_THRESHOLD_MS) {
      return next();
    }

    const apiClientInstance = await apiClient({ authenticated: false });

    const { data: refreshTokenData } = await safeInvoke(
      apiClientInstance.post<TLAIRefreshTokenResponse>(
        '/auth/refresh',
        createFormData({ refresh_token: session.authentication.refreshToken }),
      ),
      {
        onError: (error) =>
          console.error(
            '[MIDDLEWARE | AUTH TOKEN REFRESH] Error refreshing token',
            extractHTTPError(error, 'An unknown error occurred while refreshing token', true),
          ),
      },
    );

    const result = await next();

    try {
      const updatedResult = {
        ...result,
        response: mutateResponse(result.response, (headers) => {
          if (msUntilExpiry <= 0) {
            return clearSessionDataHeaders(headers);
          }

          if (refreshTokenData) {
            return updateSessionDataHeaders(headers, { ...session, ...authResponseToSessionData(refreshTokenData) });
          }

          return headers;
        }),
      };

      return updatedResult;
    } catch (error) {
      console.error('[MIDDLEWARE | AUTH TOKEN REFRESH] Error updating session data headers', error);
      return result;
    }
  });
